import { call, post } from "@/services/api";

// His "Unité n° x/y": the articles of the unit being made — empty for a kit made with the +1 counter.
export type ChecklistRow = {
  reference: string; per_unit: number; checked: boolean; taken: number | null; in_stock: number;
  // His sub-kit group (a MUSA kit's headers): its key, label and colour.
  group?: string; group_label?: string; group_color?: string;
};
export type Order = {
  checklist?: ChecklistRow[];
  id: number; kit_code: string; kit_label: string | null;
  myotaku_warehouse_id: number; qty: number; done_count: number; stock_credited: number;
  unit_minutes: number | null; status: string; assigned_to: string | null;
  started_by: string | null; started_at: string | null; finished_at: string | null;
  estimated_minutes: number; worked_minutes: number;
  minutes_by_person: Record<string, number>; note: string | null;
  // His "⛔ Arrêter et envoyer l'alerte" — set while the preparation is stopped.
  open_stop: Stop | null;
  // His owner of a running / paused order (others must take over), a split's origin, the relays.
  owner: string | null; split_from_id: number | null; created_at: string;
  relays: { from: string | null; to: string; at: string }[];
  // His card: who finished it, since when and by whom it is paused, who resumed it, how many pauses.
  finished_by?: string | null; paused_at?: string | null; paused_by?: string | null; resumed_by?: string | null; pause_count?: number;
};
export type Stop = {
  id: number; production_order_id: number; reason: string; detail: string | null; stopped_by: string | null;
  stopped_at: string; resolved_at: string | null; resolved_by: string | null; resolution: string | null;
  kit_label?: string | null; qty?: number; done_count?: number;
  // Whether the responsable's Slack alert went out.
  alert?: "sent" | "failed" | "pending";
};
// His STOP_REASONS, verbatim — the server refuses anything else.
export const STOP_REASONS = ["Manque de matériel / d'article", "Plus de cartons", "Plus de stock", "Autre problème"] as const;
export type StockRow = {
  code: string; label: string | null; unit_minutes: number | null;
  on_hand: number; reserved: number; available: number;
  by_warehouse: { myotaku_warehouse_id: number; qty: number }[];
  // His kit chip: colour, the MUSA kits' sub-kits, what one unit takes, and Penthalaz's figures
  // (its own reservations plus what Dielsdorf and Berlin lack = need).
  colour?: string | null; text_colour?: string | null; composed_of?: string[]; per_unit?: Record<string, number>;
  penthalaz?: { on_hand: number; allocated: number; to_send: number; need: number; available: number };
  // His stock card per warehouse (Penthalaz 1, Dielsdorf 67, Berlin 4), and kits ordered not yet in stock.
  warehouses?: Record<string, { qty: number; allocated: number }>;
  in_production?: number;
};
// His per-preparer row for today; `name` null is his "(no name)".
export type Preparer = {
  name: string | null; kits_made: number; worked_minutes: number; minutes_per_kit: number | null;
  given_minutes: number; pauses: number;
  now: { kit_label: string; qty: number; done_count: number; status: string; paused_at: string | null }[];
};
export type Board = {
  day: string; warehouse_id: number | null;
  // Names come from the feed. ⛔ `name` is nullable on purpose: if the feed is down the board
  // still works and falls back to ids, because everything else on it is our own data.
  warehouses: { id: number; name: string | null }[];
  progress: {
    target_kits: number; done_kits: number; percent: number;
    estimated_minutes: number; worked_minutes: number; variance_minutes: number;
    // His tiles: the team's capacity and what is left once the day's given time is taken off.
    capacity: { people: number; hours_per_day: number }; capacity_minutes: number; margin_minutes: number;
  };
  known_names: string[];
  by_preparer: Preparer[];
  queue: Order[];
  finished: Order[];
  stock: StockRow[];
  // Preparations stopped and not picked up again — the responsable's box.
  stopped: Stop[];
  allocations: {
    id: number; myotaku_event_id: number; myotaku_contract_id: number | null; client_name?: string | null;
    myotaku_warehouse_id: number | null; needed_on: string | null;
    contract_type: string | null; status: string;
    kits: Record<string, number>; articles: Record<string, number>;
    // His per-delivery sync report: "K=… A=… SRC=… T=… +T=… AT=… LIV=… ?=…".
    report_line?: string | null;
  }[];
  // His `itemStock`: Penthalaz's articles, for the new-order shortage warning.
  article_stock?: Record<string, number>;
  // His "Derniers envois": the last ten shipments from Penthalaz.
  last_sends?: { to: number; total: number; note: string | null; by: string | null; at: string; status: string; kits: { code: string; label: string | null; qty: number }[] }[];
  // His manager's "Bons de production": every order, newest first, cancelled ones too.
  orders?: Order[];
};


export const fetchBoard = (warehouseId?: number | null) =>
  call<Board>(`/api/v1/production/board${warehouseId ? `?warehouse_id=${warehouseId}` : ""}`);
export const createOrder = (b: { kit_code: string; warehouse_id: number; qty: number; assigned_to?: string; note?: string }) =>
  post<Board>(`/api/v1/production/orders`, b);
// His manager's "Supprimer" on a finished or cancelled order.
export const deleteOrder = (id: number) => call<Board>(`/api/v1/production/orders/${id}`, { method: "DELETE" });

// His stock card's entry: add a quantity, ± one, or correct the total.
export const adjustKit = (b: { kit_code: string; warehouse_id: number; delta?: number; set?: number; mode?: "plus_minus" }) =>
  post<Board>(`/api/v1/production/kit_stock`, b);

// His "Envoi de kits vers un entrepôt": several kits from Penthalaz in one shipment.
export const sendKits = (b: { to_warehouse_id: number; kits: Record<string, number>; note?: string }) =>
  post<Board & { sent: { to: number; total: number } }>(`/api/v1/production/transfers`, b);
export const startOrder = (id: number) => post<Board>(`/api/v1/production/orders/${id}/start`);
export const pauseOrder = (id: number) => post<Board>(`/api/v1/production/orders/${id}/pause`);
export const stopOrder = (id: number, reason: string, detail: string) =>
  post<Board>(`/api/v1/production/orders/${id}/stop`, { reason, detail });
export const addUnit = (id: number, delta: number) => post<Board>(`/api/v1/production/orders/${id}/units`, { delta });
export const finishOrder = (id: number) => post<Board>(`/api/v1/production/orders/${id}/finish`);
export const checkItem = (id: number, article_reference: string, checked: boolean) =>
  post<Board>(`/api/v1/production/orders/${id}/check`, { article_reference, checked });
export const validateUnit = (id: number) => post<Board>(`/api/v1/production/orders/${id}/validate_unit`);
export const cancelOrder = (id: number) => post<Board>(`/api/v1/production/orders/${id}/cancel`);
export const takeOver = (id: number) => post<Board>(`/api/v1/production/orders/${id}/take_over`);
export const assignOrder = (id: number, assigned_to: string) => post<Board>(`/api/v1/production/orders/${id}/assign`, { assigned_to });
export const splitOrder = (id: number, qty: number, assigned_to: string) =>
  post<Board>(`/api/v1/production/orders/${id}/split`, { qty, assigned_to });
export const reopenOrder = (id: number) => post<Board>(`/api/v1/production/orders/${id}/reopen`);
export const resolveStop = (id: number) => post<Board>(`/api/v1/production/stops/${id}/resolve`);
export const saveCapacity = (people: number, hours_per_day: number) =>
  call<Board>(`/api/v1/production/capacity`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ people, hours_per_day }),
  });

export type DemandSeries = {
  kit_id: number | null; code: string; label: string | null;
  // His `demandColor`: the kit's own colour (his shades for the five linen sizes).
  colour: string; by_month: number[]; total: number;
};
export type Demand = {
  months: { key: string; label: string }[];
  totals: number[];
  series: DemandSeries[];
  never_requested: { code: string; label: string | null }[];
  window: { from: string; to: string; months: number };
};

export const fetchDemand = (warehouseId: number | null) =>
  call<Demand>(`/api/v1/production/demand${warehouseId ? `?warehouse_id=${warehouseId}` : ""}`);

// His "Contrôle de cohérence du stock de kits": issues as codes and values, worded by the screen.
export type AuditIssue = { level: "err" | "warn" | "info"; code: string; vars: Record<string, string | number | null> };
export const fetchAudit = () => call<{ checked_at: string; issues: AuditIssue[] }>(`/api/v1/production/audit`);
